<template>
  <div>
    <expandabel-table
      :data="printHistory"
      :headers="headers"
      :columnsToNotSort="columnsToNotSort"
      :useSearch="false"
      :defaultSortKey="'modified'"
      :defaultSortOrder="-1"
    >
      <template v-slot:column="{ column, child }">
        <div
          v-if="
            column === 'labelName' ||
              column === 'quantity' ||
              column === 'printJobId'
          "
        >
          {{ child[column] }}
        </div>
        <div v-if="column === 'modified'">
          {{ formatDate(child[column]) }}
        </div>
        <div v-if="column === 'status'" class="flex items-center">
          <span
            class="flex w-2 h-2 mr-2 rounded-full"
            :class="getStatusColor(child[column])"
          />
          {{ child[column] }}
        </div>
        <div v-if="column === 'actions'" class="flex">
          <button
            :disabled="child.disabled"
            @click.stop="previewEntry(child)"
            class="flex flex-col items-center justify-center mr-2 bg-transparent border-none cursor-pointer text-highlight hover:underline disabled-class"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 mb-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z"
              />
            </svg>
            <span class="text-xs font-bold">Preview</span>
          </button>
          <ReprintButton
            :disabled="child.disabled"
            @click.stop="openReprint(child)"
          />
        </div>
      </template>
      <template v-slot:child="{ child }">
        <div class="flex flex-col w-full">
          <div class="flex flex-wrap p-4 rounded-lg bg-primary-light">
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">{{ child.printerName }}</span>
              <span class="text-gray-light">{{ child.printerModel }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">IP Address</span>
              <span class="text-gray-light">{{ child.printerIpAddress }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Article</span>
              <span class="text-gray-light">{{ child.articleName }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Order Number</span>
              <span class="text-gray-light">{{ child.orderReference }}</span>
            </div>
            <div class="flex flex-col w-full mt-6" v-if="child.message">
              <span class="text-xs font-bold">Message</span>
              <span class="text-gray-light">{{ displayMessage(child.message) }}</span>
            </div>
            <CollapsibleSection
              @click="child.showVariables = !child.showVariables"
              :is-open="child.showVariables"
              name="Variables"
            >
              <div
                class="flex w-full"
                v-for="(variable, dataIndex) in child.variables"
                :key="dataIndex"
              >
                <p class="flex-1">{{ variable.key }}</p>
                <p class="flex-1">{{ variable.data }}</p>
              </div>
            </CollapsibleSection>
            <template v-if="child.serialNumbers && child.serialNumbers.length">
              <CollapsibleSection
                @click="child.showSerials = !child.showSerials"
                :is-open="child.showSerials"
                name="Counter value(s)"
              >
                <div
                  class="flex w-full"
                  v-for="(serialNumber, dataIndex) in child.serialNumbers"
                  :key="dataIndex"
                >
                  <p class="flex-1">{{ serialNumber }}</p>
                  <ReprintButton
                    :disabled="child.disabled"
                    @click.stop="openReprint(child, serialNumber)"
                    class="flex-1"
                  />
                </div>
              </CollapsibleSection>
            </template>
          </div>
          <div
            class="p-4 mt-4 rounded-lg bg-primary-light"
            v-if="child.childPrintJobEntries.length > 0"
          >
            <div class="flex">
              <Header class="flex-1">Reprints</Header>
              <Header class="flex-1">Status</Header>
              <Header class="flex-1">Modified</Header>
            </div>

            <div
              class="p-4 mt-4 rounded-lg bg-primary-dark"
              v-for="(variable, dataIndex) in child.childPrintJobEntries"
              :key="dataIndex"
            >
              <div class="flex">
                <span class="flex-1">{{ variable.labelName }}</span>
                <div class="flex items-center flex-1">
                  <span
                    class="flex w-2 h-2 mr-2 rounded-full"
                    :class="getStatusColor(variable.status)"
                  />
                  {{ variable.status || 'Success' }}
                </div>
                <span class="flex-1">{{
                  variable.modified ? formatDate(variable.modified) : (variable.reprintedAt ? formatDate(variable.reprintedAt) : '-')
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </expandabel-table>
    <div class="py-2">
      <div class="float-right">
        <button
          class="text-base bg-transparent border-none disabled:cursor-not-allowed"
          @click="setPage(page - 1)"
          :disabled="page === 1"
        >
          <i class="arrow left" :class="{ 'border-gray': page === 1 }" />
        </button>
        <span class="select-none">Page {{ page }}</span>
        <button
          class="text-base bg-transparent border-none disabled:cursor-not-allowed"
          @click="setPage(page + 1)"
          :disabled="printHistory.length < pageSize"
        >
          <i
            class="arrow right"
            :class="{ 'border-gray': printHistory.length < pageSize }"
          />
        </button>
      </div>
    </div>
    <modal v-if="showPreview" type="success" @close="closePreview">
      <template slot="body">
        <div
          style="max-height: 60vh"
          class="relative flex justify-center"
        >
          <img
            class="object-contain w-full border border-solid border-gray-50/50 rounded-lg"
            :src="currentPreview"
            alt="label"
          />
        </div>
      </template>
    </modal>
    <modal
      v-if="showReprint"
      type="success"
      @close="closeReprint"
      :isFlex="true"
    >
      <template slot="header">
        <img
          class="w-full h-full p-4"
          src="../../assets/images/printer.svg"
          alt="printer"
        />
      </template>
      <template class="w-full h-full" slot="body">
        <div
          class="flex items-center justify-between w-full px-4 py-2 rounded bg-primary-light"
        >
          <p class="text-white ">{{ entryToReprint.labelName }}</p>
          <div v-if="!entryToReprint.serialNumber && (!entryToReprint.serialNumbers || entryToReprint.serialNumbers.length === 0)">
            <number-input v-model="reprintQuantity" />
          </div>
          <div v-else-if="!entryToReprint.serialNumber && entryToReprint.serialNumbers && entryToReprint.serialNumbers.length > 0">
            <p class="text-white text-sm">Will reprint all {{ entryToReprint.serialNumbers.length }} serials</p>
          </div>
        </div>
      </template>
      <template slot="buttons">
        <div class="flex items-center justify-center w-full mt-6">
          <button class="mr-8 button primary white" @click="reprint">
            Print label
          </button>
          <button class="button primary white" @click="reprintPdf">
            Print to PDF
          </button>
        </div>
      </template>
    </modal>
    <modal
      v-if="error"
      @close="error = false"
      type="error"
      heading="Error"
      :description="errorText"
    />
  </div>
</template>

<script>
import ExpandabelTable from "../ExpandabelTable.vue";
import Modal from "../Modal.vue";
import NumberInput from "../forms/NumberInput.vue";
import { formatDate } from "../../utils/dateHelpers";
import ReprintButton from "components/printhistory/ReprintButton";
import CollapsibleSection from "components/printhistory/CollapsibleSection";
import { sendToPrinterService } from "../../utils/printerHelpers";

export default {
  components: {
    CollapsibleSection,
    ReprintButton,
    ExpandabelTable,
    Modal,
    NumberInput,
  },
  props: {
    printHistory: { type: Array },
    computerName: { type: String, default: null },
    pageSize: { type: Number },
    page: { type: Number },
    setPage: { type: Function },
    loadingText: { type: String },
  },
  data() {
    return {
      headers: {
        printJobId: "Print Job #",
        labelName: "Label",
        status: "Status",
        modified: "Modified",
        quantity: "Qty.",
        actions: "",
      },
      columnsToNotSort: ["actions"],
      showPreview: false,
      currentPreview: null,
      entryToReprint: null,
      showReprint: false,
      reprintQuantity: 1,
      error: false,
      errorText: "",
    };
  },
  methods: {
    getUserDisplayName() {
      const user = this.$store.getters["AUTH/AUTH_GET_USER"];
      return (user && user.name) || (user && user.email) || 'Unknown';
    },
    displayMessage(message) {
      if (!message || typeof message !== 'string') return message;
      const toEnglish = {
        'Kunde inte hämta printfil från URL': 'Could not retrieve print file from URL',
        'Kunde inte skicka till skrivare': 'Could not send to printer',
        'Skrivaren svarade inte': 'Printer did not respond',
        'Kunde inte skriva ut': 'Could not print'
      };
      let out = message;
      Object.keys(toEnglish).forEach(function (sv) {
        if (out.indexOf(sv) !== -1) out = out.replace(sv, toEnglish[sv]);
      });
      return out;
    },
    async previewEntry(entry) {
      const oldLoadingText = this.loadingText;
      this.$emit("loading", true);
      this.$emit("loadingText", "Fetching Preview");

      const { data } = await this.$store.$api.get(
        `/api/printjobs/printjobentries/${entry.id}/preview`
      );

      this.currentPreview = data && `data:image/png;base64,${data.image}`;

      this.$emit("loading", false);
      this.$emit("loadingText", oldLoadingText);
      this.showPreview = true;
    },
    async reprint() {
      const oldLoadingText = this.loadingText;
      this.$emit("loading", true);
      
      
      // Check if this is a bulk reprint (no specific serial number)
      if (!this.entryToReprint.serialNumber && this.entryToReprint.serialNumbers && this.entryToReprint.serialNumbers.length > 0) {
        // Bulk reprint: send separate requests for each serial number
        this.$emit("loadingText", `Generating ${this.entryToReprint.serialNumbers.length} Reprints`);
        
        let successCount = 0;
        let errorCount = 0;
        const successfulSerials = [];
        
        for (const serialNumber of this.entryToReprint.serialNumbers) {
          try {
            const { data } = await this.$store.$api.put(
              "/api/printjobs/printjobentries/reprint",
              {
                printJobEntryId: this.entryToReprint.id,
                quantity: 1, // Always 1 for individual serials
                serialNumber: serialNumber,
                computerName: this.computerName,
              }
            );
            if (data && data.data) {
              await this.sendToPrinter(data.data);
              successCount++;
              successfulSerials.push(serialNumber);
            }
          } catch (e) {
            console.error(`Failed to reprint serial ${serialNumber}:`, e);
            errorCount++;
          }
        }
        
        // Update UI to show results
        const entry = this.printHistory.find(
          entry => entry.id === this.entryToReprint.id
        );
        
        // Add child entries for each successful reprint
        for (const serialNumber of successfulSerials) {
          entry.expandableData.childPrintJobEntries = [
            ...entry.expandableData.childPrintJobEntries,
            { 
              labelName: entry.labelName,
              serialNumber: serialNumber,
              reprintedBy: this.getUserDisplayName(),
              reprintedAt: new Date().toISOString()
            },
          ];
        }
        
        if (errorCount > 0) {
          this.error = true;
          this.errorText = `Reprinted ${successCount} labels successfully. ${errorCount} failed.`;
        }
        
      } else if (!this.entryToReprint.serialNumber && (!this.entryToReprint.serialNumbers || this.entryToReprint.serialNumbers.length === 0)) {
        // Printjob without serial numbers - reprint the entire job
        this.$emit("loadingText", "Generating Reprint");
        try {
          const { data } = await this.$store.$api.put(
            "/api/printjobs/printjobentries/reprint",
            {
              printJobEntryId: this.entryToReprint.id,
              quantity: this.reprintQuantity,
              serialNumber: null, // No serial numbers
              computerName: this.computerName,
            }
          );
          if (data && data.data) {
            await this.sendToPrinter(data.data);
          }
        } catch (e) {
          this.$emit("loading", false);
          this.$emit("loadingText", oldLoadingText);
          this.error = true;
          this.errorText = `Failed to add reprint, Error: ${e.data.Message}`;
        }

        const entry = this.printHistory.find(
          entry => entry.id === this.entryToReprint.id
        );
        entry.expandableData.childPrintJobEntries = [
          ...entry.expandableData.childPrintJobEntries,
          { 
            labelName: entry.labelName,
            serialNumber: null,
            reprintedBy: this.getUserDisplayName(),
            reprintedAt: new Date().toISOString()
          },
        ];
        
      } else {
        // Single reprint (specific serial number)
        this.$emit("loadingText", "Generating new Printjob");
        try {
          const { data } = await this.$store.$api.put(
            "/api/printjobs/printjobentries/reprint",
            {
              printJobEntryId: this.entryToReprint.id,
              quantity: this.reprintQuantity,
              serialNumber: this.entryToReprint.serialNumber,
              computerName: this.computerName,
            }
          );
          if (data && data.data) {
            await this.sendToPrinter(data.data);
          }
        } catch (e) {
          this.$emit("loading", false);
          this.$emit("loadingText", oldLoadingText);
          this.error = true;
          this.errorText = `Failed to add reprint, Error: ${e.data.Message}`;
        }

        const entry = this.printHistory.find(
          entry => entry.id === this.entryToReprint.id
        );
        entry.expandableData.childPrintJobEntries = [
          ...entry.expandableData.childPrintJobEntries,
          { 
            labelName: entry.labelName,
            serialNumber: this.entryToReprint.serialNumber,
            reprintedBy: this.getUserDisplayName(),
            reprintedAt: new Date().toISOString()
          },
        ];
      }

      this.restoreReprintData(oldLoadingText);
    },
    async sendToPrinter(printFile) {
      try {
        const printerHelperUri = this.$store.getters[
          "SETTINGS/PRINTER_HELPER_URI"
        ];
        await sendToPrinterService(printerHelperUri, {
          targetAddress: printFile.targetIpAddress,
          port: printFile.targetPort,
          targetPrintQueue: printFile.targetPrintQueue,
          printerFileUrl: printFile.printFileName,
          useLocalPrinterDriver: printFile.useLocalPrinterDriver,
          pdfAsBase64String: printFile.pdfAsBase64String,
          orientation: printFile.orientation,
          scale: printFile.scale,
          side: printFile.side,
          paperSize: printFile.paperSize,
          customPaperSize: printFile.customPaperSize,
        });

        await this.$store.dispatch("PRINTJOBS/UPDATE_PRINTJOBENTRY_STATUS", {
          printJobEntryId: printFile.printJobEntryId,
          status: "Success",
        });
      } catch (error) {
        await this.$store.dispatch("PRINTJOBS/UPDATE_PRINTJOBENTRY_STATUS", {
          printJobEntryId: printFile.printJobEntryId,
          status: "Error",
          message: "Could not print using printerhelper",
        });
      }
    },
    async reprintPdf() {
      const oldLoadingText = this.loadingText;
      this.$emit("loading", true);
      this.$emit("loadingText", "Generating new Printjob");
      try {
        const { data } = await this.$store.$api.put(
          "/api/printjobs/printjobentries/reprint/pdf",
          {
            printJobEntryId: this.entryToReprint.id,
            quantity: this.reprintQuantity,
            computerName: this.computerName,
            serialNumber: this.entryToReprint.serialNumber,
          },
          {
            responseType: "blob",
          }
        );

        const fileUrl = window.URL.createObjectURL(new Blob([data]));
        const fileLink = document.createElement("a");
        fileLink.target = "_blank";
        fileLink.href = fileUrl;
        fileLink.setAttribute(
          "download",
          `${this.entryToReprint.labelName}.pdf`
        );
        document.body.appendChild(fileLink);
        fileLink.click();
      } catch (e) {
        this.$emit("loading", false);
        this.$emit("loadingText", oldLoadingText);
        this.error = true;
        this.errorText = `Failed to add reprint, Error: ${e.data.Message}`;
      }

      this.restoreReprintData(oldLoadingText);
    },
    restoreReprintData(oldLoadingText) {
      this.$emit("loading", false);
      this.$emit("loadingText", oldLoadingText);
      this.showReprint = false;
      this.entryToReprint = null;
    },
    closePreview() {
      this.currentPreview = null;
      this.showPreview = false;
    },
    openReprint(entry, serialNumber = null) {
      // Use expandableData which contains the actual data with serialNumbers and childPrintJobEntries
      const expandableData = entry.expandableData || entry;
      
      // Try to get serialNumbers from childPrintJobEntries if not directly available
      let serialNumbers = expandableData.serialNumbers || [];
      if (!serialNumbers || serialNumbers.length === 0) {
        if (expandableData.childPrintJobEntries && expandableData.childPrintJobEntries.length > 0) {
          const allSerialNumbers = [];
          expandableData.childPrintJobEntries.forEach(child => {
            if (child.serialNumbers && child.serialNumbers.length > 0) {
              allSerialNumbers.push(...child.serialNumbers);
            }
          });
          serialNumbers = allSerialNumbers;
        }
      }
      
      this.entryToReprint = { 
        ...expandableData, 
        serialNumber,
        serialNumbers: serialNumbers
      };
      
      this.showReprint = true;
    },
    closeReprint() {
      this.entryToReprint = false;
      this.showReprint = false;
      this.reprintQuantity = 1;
    },
    formatDate,
    getStatusColor(status) {
      let color = "bg-highlight";

      if (status === "Success") {
        color = "bg-green-500";
      } else if (status === "Error" || status === "Canceled") {
        color = "bg-red-600";
      }

      return { [color]: true };
    },
  },
};
</script>

<style lang="postcss" scoped>
.disabled-class:disabled,
.disabled-class[disabled] {
  @apply text-gray cursor-not-allowed no-underline;
}
</style>
